import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeClass as _normalizeClass, withKeys as _withKeys, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "verify-screen" }
const _hoisted_2 = { class: "main-content" }
const _hoisted_3 = { class: "header" }
const _hoisted_4 = { class: "header-actions" }
const _hoisted_5 = { class: "selection-panel" }
const _hoisted_6 = { class: "selection-row" }
const _hoisted_7 = { class: "selection-content" }
const _hoisted_8 = { class: "selection-value" }
const _hoisted_9 = { class: "selection-row" }
const _hoisted_10 = { class: "selection-content" }
const _hoisted_11 = { class: "mode-switch" }
const _hoisted_12 = {
  key: 0,
  class: "input-wrapper"
}
const _hoisted_13 = { class: "input-with-suggestions" }
const _hoisted_14 = { class: "input-shell" }
const _hoisted_15 = {
  key: 0,
  class: "scan-wrapper"
}
const _hoisted_16 = { class: "scan-toolbar" }
const _hoisted_17 = { class: "scan-tip" }
const _hoisted_18 = { class: "scan-status" }
const _hoisted_19 = {
  key: 0,
  class: "scanner-empty"
}
const _hoisted_20 = { class: "scan-result-card" }
const _hoisted_21 = {
  key: 0,
  class: "scan-result-time"
}
const _hoisted_22 = { class: "scan-result-text" }
const _hoisted_23 = {
  key: 1,
  class: "scan-order-highlight"
}
const _hoisted_24 = {
  key: 1,
  class: "success-toast"
}

import { computed, nextTick, ref, onBeforeUnmount, onMounted } from 'vue';
  import { Html5Qrcode } from 'html5-qrcode';
  import type { CouponUseResultModel } from '@/service/order';
  import { useCustomerCoupon } from '@/service/order';
  import { fetchShopList } from '@/service/base';
  import { readSelectedShop } from '@/utils/selectedShop';
  import { fetchCategoryItemList } from '@/service/food';
  import type { ShopDetailModel } from '@/model/shop';
  import { message } from 'ant-design-vue';
  import okSoundSrc from '@/assets/audio/ok.mp3';
  import errorSoundSrc from '@/assets/audio/error.mp3';

  type VerificationMode = 'input' | 'scan';
  interface ShopFoodOption {
    shopFoodId: number;
    shopFoodName: string;
  }

  // 输入框卡券码/二维码
  const scannerContainerId = 'buffet-qrcode-reader';
  
export default /*@__PURE__*/_defineComponent({
  __name: 'BuffetVerificationView',
  setup(__props) {

  const inputCouponCode = ref('');
  const inputRef = ref<{ focus?: () => void } | null>(null);

  // 核销结果提示
  const successMessage = ref('');
  const isVerifying = ref(false);
  const verificationMode = ref<VerificationMode>('input');
  const scanQueue = ref<string[]>([]);
  const isQueueProcessing = ref(false);
  const lastScannedRawText = ref('');
  const lastScannedAt = ref('');
  const lastVerifiedFoodLines = ref<string[]>([]);

  // 店铺选择相关
  const shopList = ref<ShopDetailModel[]>([]);
  // 当前生效的店铺ID（仅来自 URL 或下拉选择）
  const currentShopId = ref<number | null>(null);
  const shopFoodOptions = ref<{ label: string; value: number }[]>([]);
  const selectedShopFoodIds = ref<number[]>([]);
  const isShopFoodLoading = ref(false);

  const currentShopName = computed(() => {
    if (!currentShopId.value) return '';
    return shopList.value.find((shop) => shop.shopId === currentShopId.value)?.shopName || '';
  });

  // H5 扫码
  const isScannerRunning = ref(false);
  let html5Qrcode: Html5Qrcode | null = null;
  const lastScanTimeMap = new Map<string, number>();
  let okAudio: HTMLAudioElement | null = null;
  let errorAudio: HTMLAudioElement | null = null;

  // 加载当前账号可见的餐厅，用于解析缓存餐厅名称。
  const loadShopList = async () => {
    try {
      const response = await fetchShopList({
        pageNum: 1,
        pageSize: 100,
        data: { shopStatus: 0 },
      });
      if (response.code === 200 && response.result && response.result.list) {
        shopList.value = response.result.list;
      }
    } catch (error) {
      console.error('加载店铺列表失败:', error);
    }
  };

  const loadShopFoodOptions = async (shopId: number) => {
    isShopFoodLoading.value = true;
    try {
      const response = await fetchCategoryItemList({
        pageNum: 1,
        pageSize: 20,
        data: {
          foodPropType: 2,
          shopId,
        },
      });

      if (response.code === 200) {
        const list = (response.result?.list || []) as ShopFoodOption[];
        shopFoodOptions.value = list
          .filter((item) => item.shopFoodId && item.shopFoodName)
          .map((item) => ({
            label: item.shopFoodName,
            value: item.shopFoodId,
          }));
        selectedShopFoodIds.value = selectedShopFoodIds.value.filter((id) =>
          shopFoodOptions.value.some((option) => option.value === id),
        );
        return;
      }

      shopFoodOptions.value = [];
      selectedShopFoodIds.value = [];
      message.error(response.msg || '加载卡券商品失败');
    } catch (error) {
      console.error('加载卡券商品失败:', error);
      shopFoodOptions.value = [];
      selectedShopFoodIds.value = [];
      message.error('加载卡券商品失败，请稍后重试');
    } finally {
      isShopFoodLoading.value = false;
    }
  };

  // 初始化
  onMounted(async () => {
    initAudioPlayers();
    await loadShopList();
    const cachedShopId = readSelectedShop();
    if (cachedShopId) {
      currentShopId.value = cachedShopId;
      await loadShopFoodOptions(cachedShopId);
    }
  });

  onBeforeUnmount(async () => {
    await stopScanner();
    okAudio?.pause();
    errorAudio?.pause();
  });

  const normalizeCouponCode = (rawText: string): string => rawText.trim();

  const showResultToast = (text: string) => {
    successMessage.value = text;
    setTimeout(() => {
      successMessage.value = '';
    }, 2000);
  };

  const initAudioPlayers = () => {
    if (!okAudio) {
      okAudio = new Audio(okSoundSrc);
      okAudio.preload = 'auto';
      okAudio.playsInline = true;
      okAudio.volume = 1;
      okAudio.load();
    }
    if (!errorAudio) {
      errorAudio = new Audio(errorSoundSrc);
      errorAudio.preload = 'auto';
      errorAudio.playsInline = true;
      errorAudio.volume = 1;
      errorAudio.load();
    }
  };

  const playSound = (audio: HTMLAudioElement | null, src: string) => {
    const target = audio ?? new Audio(src);
    target.playsInline = true;
    target.volume = 1;
    try {
      target.currentTime = 0;
      const playPromise = target.play();
      if (playPromise) {
        void playPromise.catch(() => {
          const clone = target.cloneNode(true) as HTMLAudioElement;
          clone.playsInline = true;
          clone.volume = 1;
          void clone.play().catch(() => {
            // ignore play errors
          });
        });
      }
    } catch (error) {
      // ignore play errors
    }
  };

  const unlockAudio = () => {
    initAudioPlayers();
    try {
      [okAudio, errorAudio].forEach((audio) => {
        if (!audio) return;
        audio.muted = true;
        void audio.play().then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
        });
      });
    } catch (error) {
      // ignore unlock errors
    }
  };

  const playSuccessSound = () => {
    playSound(okAudio, okSoundSrc);
  };

  const playErrorSound = () => {
    playSound(errorAudio, errorSoundSrc);
  };

  const updateLastVerifiedFoods = (result: Partial<CouponUseResultModel>) => {
    const couponName = String(result?.couponName || '').trim();
    const couponNumber = String(result?.couponNumber || '').trim();
    const couponCount = Number(result?.couponCount);
    const lines: string[] = [];

    if (couponName) {
      lines.push(
        Number.isFinite(couponCount) && couponCount > 0
          ? `${couponName} x ${couponCount}`
          : couponName,
      );
    }
    if (couponNumber) {
      lines.push(`券码：${couponNumber}`);
    }

    lastVerifiedFoodLines.value = lines;
  };

  const buildCouponUsePayload = (code: string) => {
    const normalizedCode = normalizeCouponCode(code);
    return {
      couponNumbers: normalizedCode.startsWith('1|') ? [] : [normalizedCode],
      qrcode: normalizedCode.startsWith('1|') ? normalizedCode : '',
      shopFoodIds: selectedShopFoodIds.value,
      shopId: currentShopId.value,
    };
  };

  const verifyCoupon = async (code: string) => {
    if (isVerifying.value) {
      return;
    }
    isVerifying.value = true;

    const shopId = currentShopId.value;
    if (!shopId) {
      message.warning('请从右下角快速菜单选择餐厅');
      playErrorSound();
      isVerifying.value = false;
      return;
    }

    if (!selectedShopFoodIds.value.length) {
      message.warning('请先选择卡券商品');
      playErrorSound();
      isVerifying.value = false;
      return;
    }

    try {
      const normalizedCode = normalizeCouponCode(code);
      if (!normalizedCode) {
        message.warning('请输入卡券码或二维码内容');
        playErrorSound();
        return;
      }

      const resp = await useCustomerCoupon(buildCouponUsePayload(normalizedCode));
      if (resp.code === 200) {
        const couponName = resp.result?.couponName || '卡券';
        const couponCount = Number(resp.result?.couponCount);
        const successText =
          Number.isFinite(couponCount) && couponCount > 0
            ? `${couponName} 核销成功 x ${couponCount}`
            : `${couponName} 核销成功`;
        showResultToast(successText);
        message.success(successText);
        updateLastVerifiedFoods(resp.result || {});
        playSuccessSound();
      } else {
        showResultToast(`核销失败：${resp.msg || '接口返回错误'}`);
        message.error(resp.msg || '核销失败');
        playErrorSound();
      }
    } catch (e) {
      console.error('核销异常:', e);
      showResultToast(`核销异常：${e instanceof Error ? e.message : '未知错误'}`);
      message.error('核销异常，请稍后重试');
      playErrorSound();
    } finally {
      isVerifying.value = false;
    }
  };

  const processScanQueue = async () => {
    if (isQueueProcessing.value) {
      return;
    }
    isQueueProcessing.value = true;
    try {
      while (scanQueue.value.length > 0) {
        const code = scanQueue.value.shift();
        if (!code) continue;
        inputCouponCode.value = code;
        await verifyCoupon(code);
      }
    } finally {
      inputCouponCode.value = '';
      isQueueProcessing.value = false;
    }
  };

  const pushScanToQueue = (code: string) => {
    const now = Date.now();
    const previous = lastScanTimeMap.get(code) || 0;
    // 同一个二维码短时间内会重复触发识别，这里做3秒节流去重
    if (now - previous < 3000) {
      return;
    }
    lastScanTimeMap.set(code, now);
    scanQueue.value.push(code);
    void processScanQueue();
  };

  const handleScanSuccess = (decodedText: string) => {
    const code = normalizeCouponCode(decodedText);
    if (!code) return;
    lastScannedRawText.value = decodedText;
    lastScannedAt.value = new Date().toLocaleString('zh-CN', { hour12: false });
    pushScanToQueue(code);
  };

  const isHttpsOrLocal = (): boolean => {
    const { protocol, hostname } = window.location;
    return protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1';
  };

  const startScanner = async (): Promise<boolean> => {
    if (isScannerRunning.value) return true;
    if (!isHttpsOrLocal()) {
      message.warning('扫码需要 HTTPS 环境（localhost 除外）');
      return false;
    }

    try {
      const cameras = await Html5Qrcode.getCameras();
      if (!cameras.length) {
        message.warning('未检测到可用摄像头');
        return false;
      }

      if (!html5Qrcode) {
        html5Qrcode = new Html5Qrcode(scannerContainerId);
      }

      const isMobile = window.innerWidth <= 768;
      const qrboxSize = isMobile ? 180 : 220;
      const startConfig = {
        fps: 12,
        qrbox: { width: qrboxSize, height: qrboxSize },
      };

      const startErrorCallback = () => {
        // 忽略单次识别失败，避免频繁提示影响扫码体验
      };

      // 1) 首选后置摄像头约束，移动端优先启用 environment
      let started = false;
      try {
        await html5Qrcode.start(
          { facingMode: { exact: 'environment' } },
          startConfig,
          handleScanSuccess,
          startErrorCallback,
        );
        started = true;
      } catch {
        // 2) exact 失败时再降级为非 exact 的 environment
        try {
          await html5Qrcode.start(
            { facingMode: 'environment' },
            startConfig,
            handleScanSuccess,
            startErrorCallback,
          );
          started = true;
        } catch {
          // 3) 仍失败则按设备列表匹配后置摄像头
          const backCamera =
            cameras.find((item) => /back|rear|environment|后置|后摄/gi.test(item.label)) ||
            cameras[cameras.length - 1] ||
            cameras[0];
          await html5Qrcode.start(
            backCamera.id,
            startConfig,
            handleScanSuccess,
            startErrorCallback,
          );
          started = true;
        }
      }

      if (!started) {
        throw new Error('无法启动后置摄像头');
      }

      isScannerRunning.value = true;
      return true;
    } catch (error) {
      console.error('启动扫码失败:', error);
      message.error('启动扫码失败，请检查摄像头权限');
      await stopScanner();
      return false;
    }
  };

  const stopScanner = async () => {
    scanQueue.value = [];
    lastScanTimeMap.clear();
    if (!html5Qrcode) {
      isScannerRunning.value = false;
      return;
    }
    try {
      if (html5Qrcode.isScanning) {
        await html5Qrcode.stop();
      }
      await html5Qrcode.clear();
    } catch (error) {
      console.error('关闭扫码失败:', error);
    } finally {
      isScannerRunning.value = false;
      html5Qrcode = null;
    }
  };

  const handleModeChange = async (mode: VerificationMode) => {
    unlockAudio();
    if (verificationMode.value === mode) {
      return;
    }
    verificationMode.value = mode;
    inputCouponCode.value = '';

    if (mode === 'scan') {
      await nextTick();
      const started = await startScanner();
      if (!started) {
        verificationMode.value = 'input';
        await nextTick();
        inputRef.value?.focus?.();
      }
      return;
    }

    await stopScanner();
    await nextTick();
    inputRef.value?.focus?.();
  };

  // 通过输入框触发核销
  const handleVerifyInput = async () => {
    unlockAudio();
    const couponCode = inputCouponCode.value.trim();
    if (!couponCode) {
      message.warning('请输入卡券码或二维码内容');
      return;
    }
    await verifyCoupon(couponCode);
    inputCouponCode.value = '';
  };

  const handleClearInput = () => {
    inputCouponCode.value = '';
    inputRef.value?.focus?.();
  };

return (_ctx: any,_cache: any) => {
  const _component_a_select = _resolveComponent("a-select")!
  const _component_a_input = _resolveComponent("a-input")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[14] || (_cache[14] = _createElementVNode("div", { class: "top-decoration" }, null, -1)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[9] || (_cache[9] = _createStaticVNode("<div class=\"title-wrapper\" data-v-799ba4a5><div class=\"title-icon\" data-v-799ba4a5><i class=\"fas fa-utensils\" data-v-799ba4a5></i></div><div class=\"title-text\" data-v-799ba4a5><h1 class=\"title-main\" data-v-799ba4a5>餐券核销</h1><p class=\"title-sub\" data-v-799ba4a5>Buffet Verification</p></div></div>", 1)),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _cache[5] || (_cache[5] = _createElementVNode("div", { class: "selection-label" }, "当前餐厅", -1)),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("span", _hoisted_8, _toDisplayString(currentShopName.value || '未选择餐厅'), 1)
              ])
            ]),
            _createElementVNode("div", _hoisted_9, [
              _cache[6] || (_cache[6] = _createElementVNode("div", { class: "selection-label" }, "卡券商品", -1)),
              _createElementVNode("div", _hoisted_10, [
                _createVNode(_component_a_select, {
                  value: selectedShopFoodIds.value,
                  "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((selectedShopFoodIds).value = $event)),
                  mode: "multiple",
                  class: "coupon-select",
                  options: shopFoodOptions.value,
                  loading: isShopFoodLoading.value,
                  disabled: !currentShopId.value,
                  "max-tag-count": 3,
                  placeholder: "请选择可核销的卡券商品",
                  "option-filter-prop": "label",
                  "show-search": ""
                }, null, 8, ["value", "options", "loading", "disabled"])
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("button", {
              class: _normalizeClass(["mode-btn", { active: verificationMode.value === 'input' }]),
              onClick: _cache[1] || (_cache[1] = ($event: any) => (handleModeChange('input')))
            }, " 输入核销 ", 2),
            _createElementVNode("button", {
              class: _normalizeClass(["mode-btn", { active: verificationMode.value === 'scan' }]),
              onClick: _cache[2] || (_cache[2] = ($event: any) => (handleModeChange('scan')))
            }, " 扫码核销 ", 2)
          ]),
          (verificationMode.value === 'input')
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("div", _hoisted_14, [
                    _createVNode(_component_a_input, {
                      ref_key: "inputRef",
                      ref: inputRef,
                      value: inputCouponCode.value,
                      "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((inputCouponCode).value = $event)),
                      class: "order-input",
                      placeholder: "输入卡券码或二维码内容后回车核销",
                      onKeyup: _withKeys(handleVerifyInput, ["enter"])
                    }, null, 8, ["value"]),
                    (inputCouponCode.value)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          class: "clear-btn",
                          type: "button",
                          onMousedown: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["prevent"])),
                          onClick: handleClearInput
                        }, _cache[7] || (_cache[7] = [
                          _createElementVNode("i", { class: "fas fa-times" }, null, -1)
                        ]), 32))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _createElementVNode("button", {
                  class: "verify-btn",
                  onClick: handleVerifyInput
                }, _cache[8] || (_cache[8] = [
                  _createElementVNode("i", { class: "fas fa-check-circle mr-2" }, null, -1),
                  _createTextVNode("核销 ", -1)
                ]))
              ]))
            : _createCommentVNode("", true)
        ])
      ]),
      (verificationMode.value === 'scan')
        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
            _createElementVNode("div", _hoisted_16, [
              _createElementVNode("p", _hoisted_17, " 摄像头已持续扫描，可连续扫码核销（待处理：" + _toDisplayString(scanQueue.value.length) + "） ", 1),
              _createElementVNode("p", _hoisted_18, _toDisplayString(isScannerRunning.value ? '扫描中' : '摄像头未启动'), 1)
            ]),
            _createElementVNode("div", {
              class: _normalizeClass(["scanner-panel", { idle: !isScannerRunning.value }])
            }, [
              _createElementVNode("div", {
                id: scannerContainerId,
                class: "scanner-view"
              }),
              (!isScannerRunning.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_19, _cache[10] || (_cache[10] = [
                    _createElementVNode("span", null, "无法启动摄像头，请检查 HTTPS、摄像头权限或设备可用性", -1)
                  ])))
                : _createCommentVNode("", true)
            ], 2),
            _createElementVNode("div", _hoisted_20, [
              _cache[12] || (_cache[12] = _createElementVNode("p", { class: "scan-result-title" }, "最近识别内容", -1)),
              (lastScannedAt.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_21, _toDisplayString(lastScannedAt.value), 1))
                : _createCommentVNode("", true),
              _createElementVNode("p", _hoisted_22, _toDisplayString(lastScannedRawText.value || '暂无识别结果'), 1),
              (lastVerifiedFoodLines.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                    _cache[11] || (_cache[11] = _createElementVNode("p", { class: "scan-order-highlight-title" }, "本次核销结果", -1)),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(lastVerifiedFoodLines.value, (item, index) => {
                      return (_openBlock(), _createElementBlock("p", {
                        key: `${item}-${index}`,
                        class: "scan-order-highlight-item"
                      }, _toDisplayString(item), 1))
                    }), 128))
                  ]))
                : _createCommentVNode("", true)
            ])
          ]))
        : _createCommentVNode("", true),
      (successMessage.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
            _cache[13] || (_cache[13] = _createElementVNode("i", { class: "fas fa-check-circle mr-2" }, null, -1)),
            _createTextVNode(_toDisplayString(successMessage.value), 1)
          ]))
        : _createCommentVNode("", true)
    ]),
    _cache[15] || (_cache[15] = _createElementVNode("div", { class: "bottom-decoration" }, null, -1))
  ]))
}
}

})